import {
  MAP_BOOKMARKS_STORAGE_KEY,
  MAP_UI_STORAGE_KEY,
  createMapPagePersistedStateFromProjection,
} from "./map-page-state.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";

export function createMapPagePersistController({
  globalRef = globalThis,
  readProjection,
  subscribeProjection,
  isReady = () => true,
  createPersistedStateImpl = createMapPagePersistedStateFromProjection,
  uiStorageKey = MAP_UI_STORAGE_KEY,
  bookmarksStorageKey = MAP_BOOKMARKS_STORAGE_KEY,
} = {}) {
  if (typeof readProjection !== "function") {
    throw new TypeError("Map page persistence requires a projection reader");
  }
  if (typeof subscribeProjection !== "function") {
    throw new TypeError("Map page persistence requires a projection subscription");
  }
  const state = {
    persistedUiJson: "",
    persistedBookmarksJson: "",
    observedUi: null,
    observedBridged: null,
    observedBookmarks: null,
    hasBaseline: false,
    started: false,
    unsubscribe: null,
  };

  function computePersistedState(projection) {
    try {
      const persistedState = createPersistedStateImpl(
        assertMapShellProjectionV1(projection),
      );
      return typeof persistedState?.uiJson === "string"
        && typeof persistedState?.bookmarksJson === "string"
        ? persistedState
        : null;
    } catch (_error) {
      return null;
    }
  }

  function seed(projection = readProjection()) {
    let current;
    try {
      current = assertMapShellProjectionV1(projection);
    } catch (_error) {
      return false;
    }
    const persistedState = computePersistedState(current);
    if (!persistedState) {
      return false;
    }
    state.persistedUiJson = persistedState.uiJson;
    state.persistedBookmarksJson = persistedState.bookmarksJson;
    state.observedUi = current.ui;
    state.observedBridged = current.bridged;
    state.observedBookmarks = current.bookmarks;
    state.hasBaseline = true;
    return true;
  }

  function persistProjection(projection) {
    try {
      if (!isReady()) {
        return false;
      }
    } catch (_error) {
      return false;
    }
    const persistedState = computePersistedState(projection);
    if (!persistedState) {
      return false;
    }
    try {
      if (persistedState.uiJson !== state.persistedUiJson) {
        globalRef.localStorage?.setItem?.(uiStorageKey, persistedState.uiJson);
        state.persistedUiJson = persistedState.uiJson;
      }
      if (persistedState.bookmarksJson !== state.persistedBookmarksJson) {
        globalRef.localStorage?.setItem?.(bookmarksStorageKey, persistedState.bookmarksJson);
        state.persistedBookmarksJson = persistedState.bookmarksJson;
      }
      return true;
    } catch (_error) {
      return false;
    }
  }

  function persistNow() {
    try {
      return persistProjection(readProjection());
    } catch (_error) {
      return false;
    }
  }

  function handleProjection(projection) {
    let current;
    try {
      current = assertMapShellProjectionV1(projection);
    } catch (_error) {
      return false;
    }
    if (
      current.ui === state.observedUi
      && current.bridged === state.observedBridged
      && current.bookmarks === state.observedBookmarks
    ) {
      return false;
    }
    state.observedUi = current.ui;
    state.observedBridged = current.bridged;
    state.observedBookmarks = current.bookmarks;
    return persistProjection(current);
  }

  function handleSubscribedProjection(projection) {
    if (!state.started) {
      return false;
    }
    try {
      return state.hasBaseline ? handleProjection(projection) : seed(projection);
    } catch (_error) {
      return false;
    }
  }

  function detachSubscription() {
    const unsubscribe = state.unsubscribe;
    state.unsubscribe = null;
    state.started = false;
    state.hasBaseline = false;
    state.observedUi = null;
    state.observedBridged = null;
    state.observedBookmarks = null;
    return unsubscribe;
  }

  function start() {
    if (state.started) {
      return true;
    }
    let unsubscribe = null;
    state.started = true;
    state.hasBaseline = false;
    try {
      unsubscribe = subscribeProjection(handleSubscribedProjection, {
        emitCurrent: true,
      });
      if (typeof unsubscribe !== "function") {
        throw new TypeError(
          "Map page persistence projection subscription must return an unsubscribe function",
        );
      }
      state.unsubscribe = unsubscribe;
      if (!state.hasBaseline) {
        const stopSubscription = detachSubscription();
        stopSubscription?.();
        return false;
      }
      return true;
    } catch (error) {
      const stopSubscription = state.unsubscribe || unsubscribe;
      state.unsubscribe = null;
      state.started = false;
      state.hasBaseline = false;
      state.observedUi = null;
      state.observedBridged = null;
      state.observedBookmarks = null;
      stopSubscription?.();
      throw error;
    }
  }

  function stop() {
    if (!state.started) {
      return;
    }
    const unsubscribe = detachSubscription();
    unsubscribe?.();
  }

  return Object.freeze({
    persistNow,
    start,
    stop,
  });
}
